import React, { useState, useEffect } from 'react';
import { XMarkIcon, UserGroupIcon, BriefcaseIcon, CheckIcon, XCircleIcon } from '@heroicons/react/24/outline';
import LeadAssignmentService from '../services/leadAssignmentService';
import { employeeService } from '../services/employeeService';
import { departmentService } from '../services/departmentService';
import { useAuth } from '../context/AuthContext';
import LeadAssignmentNotificationService from '../services/leadAssignmentNotificationService';
import { authApi } from '../config/api';

const ResponsiveSelect = ({ value, onChange, options, placeholder, name, required, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOpt = options.find(o => String(o.value) === String(value));
  const displayLabel = selectedOpt ? selectedOpt.label : placeholder;

  return (
    <div className="w-full relative">
      {/* Desktop Native Select */}
      <select
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        className="hidden md:block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
      >
        <option value="">{placeholder}</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>

      {/* Mobile Custom Select Overlay */}
      <div className="md:hidden block w-full relative" ref={wrapperRef}>
        <div
          className={`w-full px-2 py-2 border border-gray-300 rounded-lg focus:outline-none bg-white flex justify-between items-center shadow-sm ${disabled ? 'opacity-70 bg-gray-100 cursor-not-allowed' : 'cursor-pointer'}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div className="text-left truncate text-gray-700 text-xs">{displayLabel}</div>
          <svg className={`w-3.5 h-3.5 text-gray-500 flex-shrink-0 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
        </div>
        {isOpen && !disabled && (
          <div className="absolute z-[99999] mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-2xl max-h-[250px] overflow-y-auto left-0 right-0 py-1">
            <div
              className={`px-3 py-2 border-b border-gray-100 text-xs active:bg-gray-100 transition-colors ${!value ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'}`}
              onClick={() => {
                onChange({ target: { name: name || '', value: '' } });
                setIsOpen(false);
              }}
            >
              {placeholder}
            </div>
            {options.map(opt => (
              <div
                key={opt.value}
                className={`px-3 py-2 border-b border-gray-100 whitespace-normal break-words text-xs leading-tight active:bg-gray-100 transition-colors ${String(value) === String(opt.value) ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'}`}
                onClick={() => {
                  onChange({ target: { name: name || '', value: opt.value } });
                  setIsOpen(false);
                }}
              >
                {opt.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const EnhancedLeadAssignmentModal = ({ 
  isOpen, 
  onClose, 
  selectedLeads = [], 
  onAssignmentComplete 
}) => {
  const { user } = useAuth();
  
  // State for company-wise organization
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]); // Multiple selection
  const [priority, setPriority] = useState('medium');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('department');

  useEffect(() => {
    if (isOpen) {
      loadAllData();
      // Pre-select currently assigned employees
      if (selectedLeads.length === 1) {
        const lead = selectedLeads[0];
        if (lead.assignedEmployees && lead.assignedEmployees.length > 0) {
          setSelectedEmployees(lead.assignedEmployees.map(emp => emp.employeeId || emp._id));
        } else if (lead.assignedTo) {
          const assignedIds = Array.isArray(lead.assignedTo) ? lead.assignedTo : [lead.assignedTo];
          setSelectedEmployees(assignedIds);
        }
      }
    }
  }, [isOpen, selectedLeads]);

  // Load all data
  const loadAllData = async () => {
    setLoadingData(true);
    setError('');
    
    try {
      const employeesByDeptRes = await employeeService.getEmployeesByDepartments();
      
      if (employeesByDeptRes.success) {
        setEmployees(employeesByDeptRes.data || []);
        setDepartments(employeesByDeptRes.departments || []);
      } else {
        const employeesRes = await employeeService.getAllEmployees();
        if (employeesRes.success) {
          setEmployees(employeesRes.data || []);
        }
        
        const departmentsRes = await departmentService.getAllDepartments();
        if (departmentsRes.success) {
          setDepartments(departmentsRes.data || []);
        }
      }
      
      if (user && user.companyId) {
        setCompanies([{
          _id: user.companyId,
          businessName: user.businessName || 'Your Company'
        }]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load employee data');
    }
    
    setLoadingData(false);
  };

  // Toggle employee selection
  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees(prev => {
      if (prev.includes(employeeId)) {
        return prev.filter(id => id !== employeeId);
      } else {
        return [...prev, employeeId];
      }
    });
  };

  // Check if employee is selected
  const isEmployeeSelected = (employeeId) => {
    return selectedEmployees.includes(employeeId);
  };

  // Get currently assigned employees for display
  const getCurrentlyAssigned = () => {
    if (selectedLeads.length === 1) {
      const lead = selectedLeads[0];
      return lead.assignedEmployees || [];
    }
    return [];
  };

  const handleAssignment = async () => {
    if (selectedEmployees.length === 0) {
      setError('Please select at least one employee');
      return;
    }

    if (selectedLeads.length === 0) {
      setError('No leads selected for assignment');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Get employee details for selected employees
      const selectedEmployeeDetails = employees
        .filter(emp => selectedEmployees.includes(emp._id))
        .map(emp => ({
          employeeId: emp._id,
          name: emp.teamMemberName || emp.name,
          email: emp.teamMemberEmail || emp.email || '',
          phone: emp.mobileNumber || emp.phone || ''
        }));

      // Check if this is a reassignment
      const isReassignment = selectedLeads.length === 1 && currentlyAssigned.length > 0;
      
      // Calculate added and removed employees
      let addedEmployees = [];
      let removedEmployees = [];
      let keptEmployees = [];
      
      if (isReassignment) {
        const currentIds = currentlyAssigned.map(e => e.employeeId || e._id);
        
        // Find removed employees (were assigned but not selected now)
        removedEmployees = currentlyAssigned.filter(e => 
          !selectedEmployees.includes(e.employeeId || e._id)
        );
        
        // Find kept employees (were assigned and still selected)
        keptEmployees = currentlyAssigned.filter(e => 
          selectedEmployees.includes(e.employeeId || e._id)
        );
        
        // Find added employees (newly selected, not previously assigned)
        addedEmployees = selectedEmployeeDetails.filter(e => 
          !currentIds.includes(e.employeeId)
        );
      }
      
      // Build assignment notes
      let assignmentNotes = notes.trim();
      if (!assignmentNotes && isReassignment) {
        const parts = [];
        if (removedEmployees.length > 0) {
          parts.push(`Removed: ${removedEmployees.map(e => e.name).join(', ')}`);
        }
        if (addedEmployees.length > 0) {
          parts.push(`Added: ${addedEmployees.map(e => e.name).join(', ')}`);
        }
        if (keptEmployees.length > 0) {
          parts.push(`Kept: ${keptEmployees.map(e => e.name).join(', ')}`);
        }
        assignmentNotes = parts.join(' | ');
      } else if (!assignmentNotes) {
        assignmentNotes = `Assignment of ${selectedLeads.length} leads to ${selectedEmployees.length} employees`;
      }
      
      const assignmentData = {
        leadIds: selectedLeads.map(lead => lead._id || lead.id),
        assignedTo: selectedEmployees,
        assignedEmployees: selectedEmployeeDetails,
        assignmentNotes,
        priority,
        isReassignment,
        previousAssignment: isReassignment ? currentlyAssigned : null,
        addedEmployees,
        removedEmployees,
        keptEmployees
      };

      const result = await LeadAssignmentService.bulkAssignLeads(assignmentData);

      if (result.success) {
        // Send notifications to all selected employees
        for (const employeeId of selectedEmployees) {
          const leadCount = selectedLeads.length;
          const firstLead = selectedLeads[0];
          const leadName = firstLead ? firstLead.name : 'a lead';

          // Global Notification via authApi for dropdown bell and popup display
          try {
            await authApi.post('/notifications/bulk', {
              recipients: [employeeId],
              type: 'lead',
              title: 'Lead Assigned',
              message: leadCount === 1
                ? `Lead "${leadName}" has been assigned to you.`
                : `${leadCount} leads have been assigned to you.`,
              meta: {
                leadId: leadCount === 1 ? firstLead._id || firstLead.id : null,
                companyName: firstLead ? firstLead.companyName || firstLead.company || '' : ''
              }
            });
          } catch (err) {
            console.error(`Failed to send global notification to ${employeeId}:`, err);
          }

          // Lead-specific notification via lead service
          try {
            await LeadAssignmentNotificationService.createAssignmentNotification({
              leadId: leadCount === 1 ? firstLead._id || firstLead.id : null,
              leadIds: leadCount > 1 ? selectedLeads.map(l => l._id || l.id) : null,
              assignedTo: employeeId,
              notes: assignmentNotes || 'Enhanced lead assignment',
              priority: priority,
              leadName: leadCount === 1 ? leadName : `${leadCount} Leads`,
              leadEmail: leadCount === 1 && firstLead ? firstLead.email || '' : '',
              leadPhone: leadCount === 1 && firstLead ? firstLead.phone || '' : ''
            });
          } catch (err) {
            console.error(`Failed to create assignment notification for ${employeeId}:`, err);
          }
        }

        window.dispatchEvent(new Event('lead-assigned'));

        let message = '';
        if (isReassignment) {
          const messageParts = [];
          if (addedEmployees.length > 0) {
            messageParts.push(`Added: ${addedEmployees.map(e => e.name).join(', ')}`);
          }
          if (removedEmployees.length > 0) {
            messageParts.push(`Removed: ${removedEmployees.map(e => e.name).join(', ')}`);
          }
          if (keptEmployees.length > 0) {
            messageParts.push(`Kept: ${keptEmployees.map(e => e.name).join(', ')}`);
          }
          message = `Lead reassigned - ${messageParts.join(' • ')}`;
        } else {
          message = `Successfully assigned ${selectedLeads.length} leads to ${selectedEmployees.length} employees`;
        }
          
        onAssignmentComplete?.({
          success: true,
          message,
          assignedCount: selectedLeads.length,
          isReassignment,
          addedCount: addedEmployees.length,
          removedCount: removedEmployees.length
        });
        handleClose();
      } else {
        setError(result.message || 'Assignment failed');
      }
    } catch (error) {
      console.error('Assignment error:', error);
      setError('Failed to assign leads');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedDepartment('');
    setSelectedEmployees([]);
    setPriority('medium');
    setNotes('');
    setError('');
    setViewMode('department');
    onClose();
  };

  // Get filtered employees based on view mode and department
  const getFilteredEmployees = () => {
    let filtered = employees;
    
    if (viewMode === 'department' && selectedDepartment) {
      filtered = filtered.filter(emp => {
        if (!emp.department) return false;
        
        if (Array.isArray(emp.department)) {
          return emp.department.some(dept => {
            const deptId = typeof dept === 'object' ? dept._id : dept;
            return deptId === selectedDepartment;
          });
        }
        
        const empDeptId = typeof emp.department === 'object' ? emp.department._id : emp.department;
        return empDeptId === selectedDepartment;
      });
    }
    
    return filtered;
  };

  if (!isOpen) return null;

  const currentlyAssigned = getCurrentlyAssigned();
  const filteredEmployees = getFilteredEmployees();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-2 md:p-0">
      <div className="bg-white rounded-lg md:rounded-xl shadow-2xl w-full max-w-3xl mx-auto md:mx-4 max-h-[95vh] md:max-h-[90vh] overflow-hidden relative z-[10000] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-3 py-2.5 md:px-6 md:py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 md:space-x-3">
              <UserGroupIcon className="h-5 w-5 md:h-6 md:w-6 text-white" />
              <h2 className="text-[13px] md:text-xl font-semibold text-white">
                Assign Leads to Sales Team
              </h2>
            </div>
            <button onClick={handleClose} className="text-white/80 hover:text-white">
              <XMarkIcon className="h-5 w-5 md:h-6 md:w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 md:p-6 space-y-3 md:space-y-5 overflow-y-auto flex-1">
          {/* Selected Leads Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 md:p-4">
            <h3 className="text-[11px] md:text-sm font-medium text-blue-900 mb-1.5 md:mb-2">
              Selected Leads ({selectedLeads.length})
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {selectedLeads.slice(0, 5).map((lead, index) => (
                <span key={lead._id || index} className="inline-flex items-center px-1.5 md:px-2.5 py-0.5 rounded text-[10px] md:text-xs font-medium bg-blue-100 text-blue-800">
                  {lead.name}
                </span>
              ))}
              {selectedLeads.length > 5 && (
                <span className="inline-flex items-center px-1.5 md:px-2.5 py-0.5 rounded text-[10px] md:text-xs font-medium bg-gray-100 text-gray-800">
                  +{selectedLeads.length - 5} more
                </span>
              )}
            </div>
          </div>

          {/* Currently Assigned - Show only for single lead */}
          {selectedLeads.length === 1 && currentlyAssigned.length > 0 && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-2.5 md:p-4">
              <h3 className="text-[11px] md:text-sm font-semibold text-amber-900 mb-2 md:mb-3 flex items-center gap-1.5">
                <UserGroupIcon className="h-4 w-4 md:h-5 md:w-5" />
                Currently Assigned To ({currentlyAssigned.length})
              </h3>
              <div className="flex flex-wrap gap-1.5 md:gap-2 mb-2 md:mb-3">
                {currentlyAssigned.map((emp, idx) => {
                  const empId = emp.employeeId || emp._id;
                  const isStillSelected = selectedEmployees.includes(empId);
                  
                  return (
                    <div key={idx} className={`inline-flex items-center gap-1.5 md:gap-2 px-2 py-1 md:px-3 md:py-2 rounded-lg text-[10px] md:text-sm font-medium border-2 shadow-sm transition-all ${
                      isStillSelected 
                        ? 'bg-white border-amber-400' 
                        : 'bg-gray-100 border-gray-300 opacity-60'
                    }`}>
                      <div className={`w-5 h-5 md:w-7 md:h-7 rounded-full flex items-center justify-center text-[9px] md:text-xs font-bold text-white ${
                        isStillSelected ? 'bg-amber-600' : 'bg-gray-400'
                      }`}>
                        {(emp.name || 'E').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex flex-col">
                        <span className={`font-semibold leading-tight ${isStillSelected ? 'text-gray-900' : 'text-gray-500 line-through'}`}>
                          {emp.name}
                        </span>
                        {emp.email && <span className="text-[9px] md:text-xs text-gray-600 leading-tight hidden md:block">{emp.email}</span>}
                      </div>
                      {isStillSelected ? (
                        <button
                          onClick={() => toggleEmployeeSelection(empId)}
                          className="ml-0.5 md:ml-2 p-0.5 hover:bg-red-100 rounded-full transition-colors group"
                          title="Remove from assignment"
                        >
                          <XCircleIcon className="h-3.5 w-3.5 md:h-5 md:w-5 text-red-500 group-hover:text-red-700" />
                        </button>
                      ) : (
                        <button
                          onClick={() => toggleEmployeeSelection(empId)}
                          className="ml-0.5 md:ml-2 p-0.5 hover:bg-green-100 rounded-full transition-colors group"
                          title="Keep in assignment"
                        >
                          <CheckIcon className="h-3.5 w-3.5 md:h-5 md:w-5 text-green-500 group-hover:text-green-700" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="bg-amber-100 border border-amber-300 rounded-md p-2 md:p-3">
                <p className="text-[10px] md:text-sm text-amber-900 font-medium mb-0.5 md:mb-1">
                  🔄 Reassignment Mode
                </p>
                <p className="text-[9px] md:text-xs text-amber-800 leading-tight md:leading-normal">
                  • Click ❌ to remove current employees from assignment<br/>
                  • Click ✓ to keep them in assignment<br/>
                  • Select new employees below to add them to the assignment
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* View Mode Toggle */}
          <div className="flex space-x-2 md:space-x-3">
            <button
              onClick={() => { setViewMode('department'); setSelectedDepartment(''); }}
              className={`flex-1 px-2 py-1.5 md:px-4 md:py-2.5 rounded-lg text-[11px] md:text-sm font-semibold transition-all ${
                viewMode === 'department'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-blue-400'
              }`}
            >
              <BriefcaseIcon className="h-3.5 w-3.5 md:h-4 md:w-4 inline mr-1 md:mr-2" />
              Department Wise
            </button>
            <button
              onClick={() => { setViewMode('all'); setSelectedDepartment(''); }}
              className={`flex-1 px-2 py-1.5 md:px-4 md:py-2.5 rounded-lg text-[11px] md:text-sm font-semibold transition-all ${
                viewMode === 'all'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-blue-400'
              }`}
            >
              <UserGroupIcon className="h-3.5 w-3.5 md:h-4 md:w-4 inline mr-1 md:mr-2" />
              All Employees
            </button>
          </div>

          {loadingData ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading...</span>
            </div>
          ) : (
            <>
              {/* Department Selection */}
              {viewMode === 'department' && (
                <div>
                  <label className="block text-[11px] md:text-sm font-medium text-gray-700 mb-1 md:mb-2">
                    🏢 Select Department
                  </label>
                  <ResponsiveSelect
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    options={departments.map(dept => ({ value: dept._id, label: dept.name }))}
                    placeholder="Choose a department..."
                  />
                </div>
              )}

              {/* Employee Multi-Select Grid */}
              <div>
                <div className="flex items-center justify-between mb-1.5 md:mb-3">
                  <label className="text-[11px] md:text-sm font-medium text-gray-700 flex flex-col md:flex-row md:items-center gap-1">
                    <span>👥 Select Employees (Multiple)</span>
                    {selectedEmployees.length > 0 && (
                      <span className="px-1.5 py-0.5 bg-green-100 text-green-800 rounded text-[9px] md:text-xs font-semibold self-start md:self-auto">
                        {selectedEmployees.length} selected
                      </span>
                    )}
                  </label>
                  {selectedEmployees.length > 0 && (
                    <button
                      onClick={() => setSelectedEmployees([])}
                      className="text-[10px] md:text-xs text-red-600 hover:text-red-800 flex items-center gap-0.5 md:gap-1"
                    >
                      <XCircleIcon className="h-3.5 w-3.5 md:h-4 md:w-4" />
                      Clear All
                    </button>
                  )}
                </div>

                {(viewMode === 'department' && !selectedDepartment) ? (
                  <p className="text-[11px] md:text-sm text-amber-600 py-3 md:py-4 text-center bg-amber-50 rounded-lg">
                    ⚠️ Please select a department first to see employees
                  </p>
                ) : filteredEmployees.length === 0 ? (
                  <p className="text-[11px] md:text-sm text-gray-500 py-3 md:py-4 text-center bg-gray-50 rounded-lg">
                    No employees found
                  </p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 md:gap-2 max-h-48 md:max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-1.5 md:p-3 bg-gray-50">
                    {filteredEmployees.map((employee) => {
                      const isSelected = isEmployeeSelected(employee._id);
                      const isCurrentlyAssigned = currentlyAssigned.some(
                        emp => emp.employeeId === employee._id || emp._id === employee._id
                      );
                      
                      return (
                        <div
                          key={employee._id}
                          onClick={() => toggleEmployeeSelection(employee._id)}
                          className={`flex items-center gap-2 md:gap-3 p-1.5 md:p-3 rounded-lg cursor-pointer transition-all border-2 ${
                            isSelected
                              ? 'bg-blue-50 border-blue-500 shadow-sm'
                              : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                          }`}
                        >
                          {/* Checkbox */}
                          <div className={`w-4 h-4 md:w-5 md:h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                          }`}>
                            {isSelected && <CheckIcon className="h-2.5 w-2.5 md:h-3 md:w-3 text-white" />}
                          </div>
                          
                          {/* Avatar */}
                          <div className={`w-6 h-6 md:w-9 md:h-9 rounded-full flex items-center justify-center text-white text-[10px] md:text-sm font-medium flex-shrink-0 ${
                            isSelected ? 'bg-blue-600' : 'bg-gray-400'
                          }`}>
                            {(employee.teamMemberName || employee.name || 'E').charAt(0).toUpperCase()}
                          </div>
                          
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 md:gap-2">
                              <span className="font-medium text-gray-900 text-[11px] md:text-sm truncate leading-tight">
                                {employee.teamMemberName || employee.name}
                              </span>
                              {isCurrentlyAssigned && (
                                <span className="px-1 py-0.5 bg-amber-100 text-amber-700 text-[9px] md:text-xs rounded font-medium">
                                  Current
                                </span>
                              )}
                            </div>
                            <div className="text-[9px] md:text-xs text-gray-500 truncate leading-tight mt-0.5">
                              {employee.teamMemberEmail || employee.email || employee.mobileNumber || 'No contact'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Priority Selection */}
              <div>
                <label className="block text-[11px] md:text-sm font-medium text-gray-700 mb-1 md:mb-2">Priority Level</label>
                <ResponsiveSelect
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  options={[
                    { value: 'low', label: 'Low Priority' },
                    { value: 'medium', label: 'Medium Priority' },
                    { value: 'high', label: 'High Priority' },
                    { value: 'urgent', label: 'Urgent' }
                  ]}
                  placeholder="Select Priority Level"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-[11px] md:text-sm font-medium text-gray-700 mb-1 md:mb-2">Assignment Notes (Optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-2 py-1.5 md:px-3 md:py-2 text-xs md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Add any specific instructions..."
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col md:flex-row items-center justify-between p-2 md:p-4 border-t border-gray-200 bg-gray-50 gap-2 md:gap-0 flex-shrink-0">
          <div className="text-[10px] md:text-sm text-gray-600 w-full md:w-auto text-center md:text-left">
            {selectedEmployees.length > 0 && (
              <span className="text-green-600 font-medium">
                ✓ {selectedEmployees.length} employee{selectedEmployees.length > 1 ? 's' : ''} selected
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2 md:space-x-3 w-full md:w-auto">
            <button
              onClick={handleClose}
              className="flex-1 md:flex-none px-3 py-1.5 md:px-4 md:py-2 text-[11px] md:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-center"
            >
              Cancel
            </button>
            <button
              onClick={handleAssignment}
              disabled={loading || selectedEmployees.length === 0 || loadingData}
              className="flex-1 md:flex-none px-3 py-1.5 md:px-6 md:py-2 text-[11px] md:text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading && <div className="animate-spin rounded-full h-3 w-3 md:h-4 md:w-4 border-b-2 border-white mr-1.5 md:mr-2"></div>}
              {loading 
                ? (currentlyAssigned.length > 0 ? 'Reassigning...' : 'Assigning...') 
                : (currentlyAssigned.length > 0 
                    ? `Reassign to ${selectedEmployees.length}`
                    : `Assign to ${selectedEmployees.length}`
                  )
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedLeadAssignmentModal;
